/**
 * Org hub: types and the pure projections behind /o/:slug/manage.
 *
 * Overview, Board and Calendar are all views of ONE read, org_quest_rollup
 * (20261008100000), so the arithmetic lives here and the screens only draw.
 * Pure; adapters do the I/O.
 */
import type { ContentI18n } from "@/lib/questContent/types";
import { canContribute, canManageMembers, type MemberPresence, type OrgInvite, type OrgRole } from "@/lib/organizations";
import type { QuestStatus, TaskStatus } from "@/lib/types";
import { dayKey } from "@/lib/calendar";

// ─── Sections ───────────────────────────────────────────────────────────────
export type OrgSection =
  | "overview" | "board" | "calendar"
  | "chat" | "polls" | "files"
  | "members" | "invites" | "requests"
  | "settings";

export type OrgSectionGroup = "home" | "work" | "team" | "people" | "admin";

/** The lowest org level a section opens for (Simple = every member). */
export type OrgSectionAccess = "member" | "contributor" | "admin";

export interface OrgSectionDef {
  id: OrgSection;
  group: OrgSectionGroup;
  access: OrgSectionAccess;
}

/**
 * Nav order. A section's access is the lowest level its RPCs serve: Invites
 * opens to Medio (they invite as Simple and see their own invites), Requests
 * and Settings stay admin-only.
 */
export const ORG_SECTIONS: readonly OrgSectionDef[] = [
  { id: "overview", group: "home", access: "member" },
  { id: "board", group: "work", access: "member" },
  { id: "calendar", group: "work", access: "member" },
  { id: "chat", group: "team", access: "member" },
  { id: "polls", group: "team", access: "member" },
  { id: "files", group: "team", access: "member" },
  { id: "members", group: "people", access: "member" },
  { id: "invites", group: "people", access: "contributor" },
  { id: "requests", group: "people", access: "admin" },
  { id: "settings", group: "admin", access: "admin" },
];

export function visibleSections(role: OrgRole | null | undefined): OrgSectionDef[] {
  if (!role) return [];
  return ORG_SECTIONS.filter((s) =>
    s.access === "member" || (s.access === "contributor" ? canContribute(role) : canManageMembers(role)));
}

/** `?tab=` → a section this role may open; anything else lands on Overview. */
export function resolveSection(tab: string | null, role: OrgRole | null | undefined): OrgSection {
  const found = visibleSections(role).find((s) => s.id === tab);
  return found ? found.id : "overview";
}

// ─── Rollup rows ────────────────────────────────────────────────────────────
export interface OrgTaskRow {
  id: string;
  title: string;
  status: TaskStatus;
  deadline: string | null;
  budgetUsd: number | null;
  assigneeId: string | null;
  assigneeName: string | null;
  sourceLang: string | null;
  i18n: ContentI18n | null;
}

export interface OrgQuestRow {
  id: string;
  title: string;
  status: QuestStatus;
  deadline: string | null;
  totalBudgetUsd: number;
  currency: string;
  creatorId: string;
  creatorName: string | null;
  createdAt: string;
  updatedAt: string;
  sourceLang: string | null;
  i18n: ContentI18n | null;
  tasks: OrgTaskRow[];
  /** A stock-listing clone, created when a worker was hired, not when the org posted. */
  isClone: boolean;
}

// ─── Board ──────────────────────────────────────────────────────────────────
export type BoardLane = "draft" | "open" | "in_progress" | "done";
export const BOARD_LANES: readonly BoardLane[] = ["draft", "open", "in_progress", "done"];

/** Where a quest sits. Cancelled quests sit in no lane (null). */
export function laneOf(status: QuestStatus): BoardLane | null {
  switch (status) {
    case "draft": return "draft";
    case "published":
    case "negotiating": return "open";
    case "locking":
    case "active": return "in_progress";
    case "completed":
    case "closed": return "done";
    default: return null;
  }
}

export interface BoardModel {
  lanes: Record<BoardLane, OrgQuestRow[]>;
  cancelled: OrgQuestRow[];
}

export function boardModel(quests: readonly OrgQuestRow[]): BoardModel {
  const lanes: Record<BoardLane, OrgQuestRow[]> = { draft: [], open: [], in_progress: [], done: [] };
  const cancelled: OrgQuestRow[] = [];
  for (const q of quests) {
    const lane = laneOf(q.status);
    if (lane) lanes[lane].push(q);
    else cancelled.push(q);
  }
  return { lanes, cancelled };
}

/** "3 of 5 tasks done" — the progress line on a board card. */
export function taskProgress(q: Pick<OrgQuestRow, "tasks">): { done: number; total: number } {
  const live = q.tasks.filter((t) => t.status !== "cancelled");
  return { done: live.filter((t) => isTaskFinished(t.status)).length, total: live.length };
}

// ─── Deadlines ──────────────────────────────────────────────────────────────
const FINISHED_TASK: ReadonlySet<TaskStatus> = new Set(["approved", "completed", "cancelled"]);
const FINISHED_QUEST: ReadonlySet<QuestStatus> = new Set(["completed", "closed", "cancelled"]);

export function isTaskFinished(status: TaskStatus): boolean {
  return FINISHED_TASK.has(status);
}

export interface DeadlineItem {
  kind: "quest" | "task";
  id: string;
  questId: string;
  title: string;
  deadline: string;
  /** Finished work stays on the calendar, drawn quietly. */
  finished: boolean;
  assigneeName: string | null;
}

/** A quest still owes its deadline: dated, unfinished (cancelled counts as finished), and past it. */
export function isQuestOverdue(q: Pick<OrgQuestRow, "deadline" | "status">, now: number): boolean {
  return !!q.deadline && !FINISHED_QUEST.has(q.status) && Date.parse(q.deadline) < now;
}

/** The same rule for a calendar item, which already folded "finished" in. */
export function isDeadlineOverdue(d: Pick<DeadlineItem, "deadline" | "finished">, now: number): boolean {
  return !d.finished && Date.parse(d.deadline) < now;
}

/**
 * The quest or task row a deadline item came from, carrying the content a
 * reader-language title is localized from. Null when the row is gone.
 */
export function deadlineSource(
  quests: readonly OrgQuestRow[],
  item: Pick<CalendarItem, "kind" | "id" | "questId">,
): { title: string; i18n: ContentI18n | null; sourceLang: string | null } | null {
  const q = quests.find((r) => r.id === item.questId);
  if (!q) return null;
  if (item.kind === "task") return q.tasks.find((x) => x.id === item.id) ?? null;
  return q;
}

/** Every dated quest and task, earliest first. */
export function deadlineItems(quests: readonly OrgQuestRow[]): DeadlineItem[] {
  const out: DeadlineItem[] = [];
  for (const q of quests) {
    if (q.status === "cancelled") continue;
    if (q.deadline) {
      out.push({ kind: "quest", id: q.id, questId: q.id, title: q.title, deadline: q.deadline,
        finished: FINISHED_QUEST.has(q.status), assigneeName: null });
    }
    for (const t of q.tasks) {
      if (!t.deadline || t.status === "cancelled") continue;
      out.push({ kind: "task", id: t.id, questId: q.id, title: t.title, deadline: t.deadline,
        finished: isTaskFinished(t.status) || FINISHED_QUEST.has(q.status), assigneeName: t.assigneeName });
    }
  }
  return out.sort((a, b) => Date.parse(a.deadline) - Date.parse(b.deadline) || a.id.localeCompare(b.id));
}

// ─── Calendar ───────────────────────────────────────────────────────────────
// The month grid itself is shared with My Work's calendar (lib/calendar).
export { dayKey, monthGrid, type CalendarCell } from "@/lib/calendar";

/** The day a quest went up on the org's behalf. */
export interface PostedItem {
  kind: "posted";
  id: string;
  questId: string;
  title: string;
  postedAt: string;
  creatorName: string | null;
}

export type CalendarItem = DeadlineItem | PostedItem;

/** The instant a calendar item sits on. */
export function itemDate(it: CalendarItem): string {
  return it.kind === "posted" ? it.postedAt : it.deadline;
}

/**
 * Every quest the org has put up, on the day it went up. Most org quests
 * carry no deadline, so without these the calendar sat empty. Drafts weren't
 * posted and cancelled quests drop off, as they do for deadlines. A stock
 * listing shows once: its clones are hires, not posts.
 */
export function postedItems(quests: readonly OrgQuestRow[]): PostedItem[] {
  return quests
    .filter((q) => !q.isClone && q.status !== "draft" && q.status !== "cancelled")
    .map((q) => ({ kind: "posted" as const, id: q.id, questId: q.id, title: q.title, postedAt: q.createdAt, creatorName: q.creatorName }));
}

/** Deadlines and posts together, earliest first. */
export function calendarItems(quests: readonly OrgQuestRow[]): CalendarItem[] {
  const all: CalendarItem[] = [...postedItems(quests), ...deadlineItems(quests)];
  return all.sort((a, b) => Date.parse(itemDate(a)) - Date.parse(itemDate(b)) || a.id.localeCompare(b.id));
}

export function groupByDay<T extends CalendarItem>(items: readonly T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const it of items) {
    const key = dayKey(new Date(itemDate(it)));
    const list = map.get(key);
    if (list) list.push(it);
    else map.set(key, [it]);
  }
  return map;
}

// ─── Overview ───────────────────────────────────────────────────────────────
const DAY_MS = 86_400_000;

export interface OrgKpis {
  open: number;
  inProgress: number;
  done: number;
  overdue: number;
  dueThisWeek: number;
  activeMembers: number;
  totalMembers: number;
}

export function orgKpis(
  quests: readonly OrgQuestRow[],
  presence: Record<string, MemberPresence> | undefined,
  totalMembers: number,
  now: number,
): OrgKpis {
  const { lanes } = boardModel(quests);
  const pending = deadlineItems(quests).filter((d) => !d.finished);
  const active = Object.values(presence ?? {}).filter(
    (p) => p.bucket === "active_now" || p.bucket === "recent" || p.bucket === "hours",
  ).length;
  return {
    open: lanes.open.length + lanes.draft.length,
    inProgress: lanes.in_progress.length,
    done: lanes.done.length,
    overdue: pending.filter((d) => isDeadlineOverdue(d, now)).length,
    dueThisWeek: pending.filter((d) => {
      const t = Date.parse(d.deadline);
      return t >= now && t < now + 7 * DAY_MS;
    }).length,
    activeMembers: active,
    totalMembers,
  };
}

export type AttentionKind = "requests" | "overdue" | "dueSoon" | "invitesExpiring" | "drafts";

export interface OrgAttentionItem {
  kind: AttentionKind;
  count: number;
  /** Where tapping the row goes. */
  section: OrgSection;
}

/**
 * The "needs you" list on Overview, most urgent first. Admin-only kinds
 * (requests, invites) are skipped for members — they couldn't act on them.
 */
export function attentionItems(input: {
  isAdmin: boolean;
  quests: readonly OrgQuestRow[];
  pendingRequests: number;
  invites: readonly Pick<OrgInvite, "state" | "expiresAt">[];
  now: number;
}): OrgAttentionItem[] {
  const { isAdmin, quests, pendingRequests, invites, now } = input;
  const pending = deadlineItems(quests).filter((d) => !d.finished);
  const items: OrgAttentionItem[] = [];
  if (isAdmin && pendingRequests > 0) items.push({ kind: "requests", count: pendingRequests, section: "requests" });
  const overdue = pending.filter((d) => isDeadlineOverdue(d, now)).length;
  if (overdue > 0) items.push({ kind: "overdue", count: overdue, section: "calendar" });
  const soon = pending.filter((d) => {
    const t = Date.parse(d.deadline);
    return t >= now && t < now + 2 * DAY_MS;
  }).length;
  if (soon > 0) items.push({ kind: "dueSoon", count: soon, section: "calendar" });
  if (isAdmin) {
    const expiring = invites.filter(
      (i) => i.state === "invited" && Date.parse(i.expiresAt) - now < 3 * DAY_MS,
    ).length;
    if (expiring > 0) items.push({ kind: "invitesExpiring", count: expiring, section: "invites" });
  }
  const drafts = quests.filter((q) => q.status === "draft").length;
  if (isAdmin && drafts > 0) items.push({ kind: "drafts", count: drafts, section: "board" });
  return items;
}

// ─── Chat ───────────────────────────────────────────────────────────────────
export interface OrgMessage {
  id: string;
  organizationId: string;
  senderId: string;
  body: string;
  createdAt: string;
}

export const ORG_MESSAGE_MAX = 4000;

/** Messages from others newer than the read cursor (null cursor = all). */
export function unreadCount(
  messages: readonly Pick<OrgMessage, "senderId" | "createdAt">[],
  lastReadAt: string | null,
  me: string | null,
): number {
  const since = lastReadAt ? Date.parse(lastReadAt) : -Infinity;
  return messages.filter((m) => m.senderId !== me && Date.parse(m.createdAt) > since).length;
}

/** Start a new sender run when the sender changes or 5 minutes pass. */
export function startsRun(prev: OrgMessage | undefined, cur: OrgMessage): boolean {
  if (!prev || prev.senderId !== cur.senderId) return true;
  return Date.parse(cur.createdAt) - Date.parse(prev.createdAt) > 5 * 60_000;
}

// ─── Polls ──────────────────────────────────────────────────────────────────
export type PollAudience = "everyone" | "admins";

export interface OrgPoll {
  id: string;
  question: string;
  options: string[];
  audience: PollAudience;
  createdBy: string;
  creatorName: string | null;
  closesAt: string | null;
  closedAt: string | null;
  createdAt: string;
  /** Per-option counts; null until the viewer has voted or the poll is over. */
  counts: number[] | null;
  myVote: number | null;
}

export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 8;

export type PollDraftResult =
  | { ok: true; question: string; options: string[] }
  | { ok: false; error: "question" | "options" | "optionLength" };

/** Same rules as create_org_poll: trimmed, 2–8 non-blank options ≤ 100 chars. */
export function cleanPollDraft(question: string, options: readonly string[]): PollDraftResult {
  const q = question.trim();
  if (q.length < 1 || q.length > 300) return { ok: false, error: "question" };
  const opts = options.map((o) => o.trim()).filter(Boolean);
  if (opts.length < POLL_MIN_OPTIONS || opts.length > POLL_MAX_OPTIONS) return { ok: false, error: "options" };
  if (opts.some((o) => o.length > 100)) return { ok: false, error: "optionLength" };
  return { ok: true, question: q, options: opts };
}

export function isPollOpen(p: Pick<OrgPoll, "closedAt" | "closesAt">, now: number): boolean {
  return !p.closedAt && (!p.closesAt || Date.parse(p.closesAt) > now);
}

/** Whole-number percentages for each option (0 when nobody voted). */
export function pollPercents(counts: readonly number[]): number[] {
  const total = counts.reduce((a, b) => a + b, 0);
  return counts.map((c) => (total === 0 ? 0 : Math.round((c / total) * 100)));
}

// ─── Files ──────────────────────────────────────────────────────────────────
export const ORG_FILES_BUCKET = "org-files";
export const ORG_FILE_MAX_BYTES = 20 * 1024 * 1024;

/** Mirrors the org-files bucket's allowed_mime_types (20261008100300). */
export const ORG_FILE_TYPES: readonly string[] = [
  "application/pdf",
  "image/png", "image/jpeg", "image/webp", "image/gif",
  "text/plain", "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

export interface OrgFile {
  id: string;
  organizationId: string;
  uploadedBy: string;
  uploaderName: string | null;
  path: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export type FileCheck = { ok: true } | { ok: false; error: "tooLarge" | "badType" };

export function checkOrgFile(file: Pick<File, "size" | "type">): FileCheck {
  if (!ORG_FILE_TYPES.includes(file.type)) return { ok: false, error: "badType" };
  if (file.size > ORG_FILE_MAX_BYTES) return { ok: false, error: "tooLarge" };
  return { ok: true };
}

/** `{orgId}/{uuid}-{safe-name}` — the folder org-files lets members write. */
export function orgFilePath(orgId: string, uuid: string, name: string): string {
  const safe = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(-80) || "file";
  return `${orgId}/${uuid}-${safe}`;
}

export type FileKind = "pdf" | "image" | "sheet" | "doc" | "slides" | "text";

export function fileKind(mime: string): FileKind {
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (mime.includes("spreadsheet") || mime === "text/csv") return "sheet";
  if (mime.includes("wordprocessing")) return "doc";
  if (mime.includes("presentation")) return "slides";
  return "text";
}

export const ORG_FILE_NAME_MAX = 200;

/** "Plan.v2.pdf" → ".pdf"; "" when the name has none. */
export function fileExtension(name: string): string {
  return /\.[A-Za-z0-9]{1,8}$/.exec(name)?.[0] ?? "";
}

export type FileRename = { ok: true; name: string } | { ok: false; error: "empty" | "tooLong" };

/**
 * The display name a rename stores. The extension belongs to the file, not
 * the label: typing "Plaza antes" over "IMG_1234.jpeg" keeps ".jpeg", so a
 * download still opens in the right app.
 */
export function cleanFileRename(original: string, input: string): FileRename {
  const ext = fileExtension(original);
  const typed = input.trim();
  const hasExt = !!ext && typed.toLowerCase().endsWith(ext.toLowerCase());
  if (!(hasExt ? typed.slice(0, -ext.length) : typed).trim()) return { ok: false, error: "empty" };
  const name = hasExt ? typed : typed + ext;
  if (name.length > ORG_FILE_NAME_MAX) return { ok: false, error: "tooLong" };
  return { ok: true, name };
}

export type FilesView = "gallery" | "list";

/** Until the viewer picks, a library that is mostly photos opens as a gallery. */
export function defaultFilesView(mimeTypes: readonly string[]): FilesView {
  const images = mimeTypes.filter((m) => fileKind(m) === "image").length;
  return images > 0 && images * 2 > mimeTypes.length ? "gallery" : "list";
}

/** 1536 → "1.5 KB". */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
